import React, { useEffect } from "react";

import {
  trackGTMEvent,
  updatePaywallCounter,
  UpdatePaywallCounterProps,
} from "lib/utils";
import { PaywallData } from "lib/types/datoBlocks";
import { useLoginRegistrationModal } from "lib/store/loginRegistrationModal";
import PaywallModal from "components/paywallModal/paywallModal";
import { SubscriberEvent } from "content-ui/subscriberEvents/types";

type ProLoginModalProps = {
  isOpen: boolean;
  setIsOpen: Function;
  roundtableEvent?: SubscriberEvent;
  onClose?: (() => (arg0: boolean) => void) | (() => void);
  variant: "roundtables" | "briefings" | "reports";
  paywall: PaywallData;
};

const variantToPaywallCounterType: {
  [key: string]: UpdatePaywallCounterProps["type"];
} = {
  roundtables: "ev",
  briefings: "br",
  reports: "re",
};

export default function ProLoginModal({
  isOpen,
  setIsOpen,
  roundtableEvent,
  onClose,
  variant,
  paywall,
}: ProLoginModalProps) {
  const handleClose = onClose ? onClose : () => setIsOpen(false);
  const openLoginModal = useLoginRegistrationModal((state) => state.open);

  useEffect(() => {
    if (isOpen) {
      const asyncFn = async () => {
        await updatePaywallCounter({
          type: variantToPaywallCounterType[variant],
          timestamp: (new Date().getTime() / 1000).toFixed(0),
        });
      };

      asyncFn();
    }
  }, [isOpen, variant]);

  return (
    <PaywallModal
      {...paywall}
      innerTestId={`login-modal`}
      open={isOpen}
      onOpenChange={handleClose}
      onLogin={() => {
        if (window.pianoTrackingId && window.Cookiebot?.consent?.statistics) {
          window.tp.log.logMicroConversion(
            window.pianoTrackingId,
            `${variant} barrier`,
            { button: "Login", url: window.location.href }
          );
        }
        trackGTMEvent({
          event: `barrier`,
          barrierType: variant,
        });
        setIsOpen(false);
        openLoginModal({ redirectUrl: window.location.href });
      }}
      onPrimaryButtonClick={() => {
        // Close behaviour: https://linear.app/sifted/issue/DEV-1248/briefing-paywall
        if (variant !== "briefings") {
          setIsOpen(false);
        }
        if (variant === "roundtables" && roundtableEvent) {
          trackGTMEvent({
            event: "Roundtable Button Click",
            roundtableEventName: roundtableEvent.title,
            roundtableEventAction: "Redirect to Typeform", // Redirects to /subscribe & hubspot site, but we wish to keep tracking strings constant
          });
        } else {
          if (window.pianoTrackingId && window.Cookiebot?.consent?.statistics) {
            window.tp.log.logMicroConversion(
              window.pianoTrackingId,
              `${variant} barrier`,
              { button: "Apply", url: window.location.href } // Button says "Find out more", but we wish to keep tracking strings constant
            );
          }
          trackGTMEvent({
            event: `barrier`,
            barrierType: variant,
          });
        }
        window.open(paywall.primaryCtaUrl, "_blank");
      }}
    />
  );
}
